import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { bookingActions } from "../../redux/slices/bookingSlice";
import { loaderActions } from "../../redux/slices/loadingSlice";
import { useAppDispatch } from "../../redux/store";
import QuestionMark from "./_QuestionMark";
import loginService from "../../services/LoginService";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import MathCaptcha from "./MathCaptcha";

interface IFormInput {
  bookingNumber: string;
  name: string;
}

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [globalError, setGlobalError] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [captchaData, setCaptchaData] = useState<any>(null);
  const form = useForm<Partial<IFormInput>>({
    defaultValues: { bookingNumber: "", name: "" },
  });

  const logicChannel = sessionStorage.getItem("mylmx-logic");

  const { control, handleSubmit, watch, formState, reset, setError } = form;
  const { errors } = formState;
  const bookingNumber = watch("bookingNumber");
  const name = watch("name");

  const { i18n, t } = useTranslation();
  const currentLang = i18n.language;

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
    sessionStorage.setItem("mylmx-lang", language);
  };

  const onSubmit: SubmitHandler<Partial<IFormInput>> = async (data) => {
    dispatch(loaderActions.showLoader());
    console.log("Form Data:", data);

    // Perform login action (e.g., API call)
    const reqBody = {
      bookingNumber,
      name,
      ...captchaData
    };

    const response = await loginService(reqBody);

    if (response === null) {
      dispatch(loaderActions.hideLoader());
      setGlobalError(t("login.loginError"));
      return;
    }

    // Store the booking in redux.
    sessionStorage.setItem("lmxBooking", JSON.stringify(response));
    dispatch(loaderActions.hideLoader());
    dispatch(bookingActions.setBookingData(response));

    // check if it's cancelled or expired
    if (response.isCanceled || response.isExpired) {
      navigate("info");
      return;
    }

    if (response.shouldConfirmEmail) {
      navigate("/confirm-email");
      return;
    }

    navigate("/booking-details");
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg space-y-4"
    >
      {/* <div
        className={`flex justify-center ${
          logicChannel !== "france" ? "hidden" : ""
        }`}
      >
        <div className="rounded-xl overflow-hidden border">
          {" "}
          <button
            type="button"
            onClick={() => handleLanguageChange("de")}
            className={`border ${
              currentLang === "de" ? "bg-blue-500 text-white" : ""
            } p-2`}
          >
            Deutsch
          </button>
          <button
            type="button"
            onClick={() => handleLanguageChange("fr")}
            className={`border ${
              currentLang === "fr" ? "bg-blue-500 text-white" : ""
            } p-2`}
          >
            Français
          </button>
        </div>
      </div> */}

      <div className="text-warning text-center">{globalError}</div>

      <Controller
        name="bookingNumber"
        control={control}
        rules={{
          required: t("login.bookingError1"),
          pattern: {
            value: logicChannel === "france" ? /^[0-9]{6,8}$/ : /^[0-9]{7,8}$/,
            message: t("login.bookingError2"),
          },
        }}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error },
        }) => (
          <TextField
            label={t("login.bookingNumber")}
            variant="outlined"
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            error={!!errors.bookingNumber}
            helperText={errors.bookingNumber?.message}
            value={value}
          />
        )}
      />

      <Controller
        name="name"
        control={control}
        rules={{
          required: t("login.lastNameError"),
        }}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error },
        }) => (
          <TextField
            label={t("login.travellerName")}
            variant="outlined"
            fullWidth
            onChange={onChange}
            onBlur={onBlur}
            error={!!errors.name}
            helperText={errors.name?.message}
            value={value}
          />
        )}
      />

      <MathCaptcha onValidate={(isValid, data) => {
        setIsCaptchaValid(isValid);
        setCaptchaData(data);
      }} />

      <Button
        type="submit"
        variant="contained"
        className="bg-secondary hover:bg-secondary-hover"
        fullWidth
        disabled={!isCaptchaValid}
      >
        {t("login.login")}
      </Button>
      <p className="text-center text-sm text-gray-500">
        {t("login.findName")} <QuestionMark />
      </p>
    </form>
  );
};

export default LoginForm;
